import { useCallback, useEffect, useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Stack,
} from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { userAnalyticsService } from '@/services/userAnalyticsService'

const InactiveUsers = () => {
  const [filterMode, setFilterMode] = useState<'period' | 'custom'>('period')
  const [period, setPeriod] = useState<'7d' | '30d' | '365d'>('7d')
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [enableDepositFilter, setEnableDepositFilter] = useState(false)
  const [depositThreshold, setDepositThreshold] = useState<string>('')
  const [depositIntervalStart, setDepositIntervalStart] = useState<Date | null>(null)
  const [depositIntervalEnd, setDepositIntervalEnd] = useState<Date | null>(null)
  const [data, setData] = useState<
    { msisdn: string; lastActivity: string | null; totalDeposits: number; totalWithdrawals: number; transactionCount: number }[]
  >([])
  const [loading, setLoading] = useState(false)
  
  // Additional filters
  const [msisdnSearch, setMsisdnSearch] = useState('')
  const [minDeposits, setMinDeposits] = useState<string>('')
  const [maxDeposits, setMaxDeposits] = useState<string>('')
  const [minWithdrawals, setMinWithdrawals] = useState<string>('')
  const [maxWithdrawals, setMaxWithdrawals] = useState<string>('')
  const [minTransactions, setMinTransactions] = useState<string>('')
  const [maxTransactions, setMaxTransactions] = useState<string>('')
  const [sortField, setSortField] = useState<'msisdn' | 'lastActivity' | 'totalDeposits' | 'totalWithdrawals' | 'transactionCount'>('lastActivity')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  const fetchInactiveUsers = useCallback(async () => {
    setLoading(true)
    try {
      const filters: {
        period?: 7 | 30 | 365
        startDate?: string
        endDate?: string
        depositThreshold?: number
        depositIntervalStart?: string
        depositIntervalEnd?: string
      } = {}

      if (filterMode === 'period') {
      const days = period === '7d' ? 7 : period === '30d' ? 30 : 365
        filters.period = days
      } else {
        if (startDate) {
          // Set to start of day in UTC
          const date = new Date(startDate)
          date.setUTCHours(0, 0, 0, 0)
          filters.startDate = date.toISOString()
        }
        if (endDate) {
          // Set to end of day in UTC
          const date = new Date(endDate)
          date.setUTCHours(23, 59, 59, 999)
          filters.endDate = date.toISOString()
        }
      }

      if (enableDepositFilter && depositThreshold && depositIntervalStart && depositIntervalEnd) {
        filters.depositThreshold = parseFloat(depositThreshold)
        // Set to start of day in UTC
        const start = new Date(depositIntervalStart)
        start.setUTCHours(0, 0, 0, 0)
        filters.depositIntervalStart = start.toISOString()
        // Set to end of day in UTC
        const end = new Date(depositIntervalEnd)
        end.setUTCHours(23, 59, 59, 999)
        filters.depositIntervalEnd = end.toISOString()
      }

      const results = await userAnalyticsService.fetchInactiveUsers(filters)
      setData(results)
    } catch (error) {
      console.error('Failed to load inactive users', error)
      setData([])
    } finally {
      setLoading(false)
    }
  }, [filterMode, period, startDate, endDate, enableDepositFilter, depositThreshold, depositIntervalStart, depositIntervalEnd])

  useEffect(() => {
    fetchInactiveUsers()
  }, [fetchInactiveUsers])

  // Client-side filtering and sorting
  const filteredAndSortedData = useMemo(() => {
    let filtered = [...data]

    // MSISDN search
    if (msisdnSearch) {
      filtered = filtered.filter((u) => u.msisdn.toLowerCase().includes(msisdnSearch.toLowerCase()))
    }

    // Deposit range filter
    if (minDeposits) {
      const min = parseFloat(minDeposits)
      if (!isNaN(min)) {
        filtered = filtered.filter((u) => u.totalDeposits >= min)
      }
    }
    if (maxDeposits) {
      const max = parseFloat(maxDeposits)
      if (!isNaN(max)) {
        filtered = filtered.filter((u) => u.totalDeposits <= max)
      }
    }

    // Withdrawal range filter
    if (minWithdrawals) {
      const min = parseFloat(minWithdrawals)
      if (!isNaN(min)) {
        filtered = filtered.filter((u) => (u.totalWithdrawals || 0) >= min)
      }
    }
    if (maxWithdrawals) {
      const max = parseFloat(maxWithdrawals)
      if (!isNaN(max)) {
        filtered = filtered.filter((u) => (u.totalWithdrawals || 0) <= max)
      }
    }

    // Transaction count range filter
    if (minTransactions) {
      const min = parseInt(minTransactions, 10)
      if (!isNaN(min)) {
        filtered = filtered.filter((u) => u.transactionCount >= min)
      }
    }
    if (maxTransactions) {
      const max = parseInt(maxTransactions, 10)
      if (!isNaN(max)) {
        filtered = filtered.filter((u) => u.transactionCount <= max)
      }
    }

    // Sorting
    filtered.sort((a, b) => {
      let aValue: any
      let bValue: any

      switch (sortField) {
        case 'msisdn':
          aValue = a.msisdn.toLowerCase()
          bValue = b.msisdn.toLowerCase()
          break
        case 'lastActivity':
          aValue = a.lastActivity ? new Date(a.lastActivity).getTime() : 0
          bValue = b.lastActivity ? new Date(b.lastActivity).getTime() : 0
          break
        case 'totalDeposits':
          aValue = a.totalDeposits
          bValue = b.totalDeposits
          break
        case 'totalWithdrawals':
          aValue = a.totalWithdrawals || 0
          bValue = b.totalWithdrawals || 0
          break
        case 'transactionCount':
          aValue = a.transactionCount
          bValue = b.transactionCount
          break
        default:
          return 0
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

    return filtered
  }, [data, msisdnSearch, minDeposits, maxDeposits, minWithdrawals, maxWithdrawals, minTransactions, maxTransactions, sortField, sortDirection])

  const handleExportCSV = () => {
    const csv = [
      ['MSISDN', 'Last Activity', 'Total Deposits', 'Total Withdrawals', 'Transaction Count'],
      ...filteredAndSortedData.map((u) => [
        u.msisdn,
        u.lastActivity ? new Date(u.lastActivity).toLocaleDateString() : 'N/A',
        u.totalDeposits.toFixed(2),
        (u.totalWithdrawals || 0).toFixed(2),
        u.transactionCount.toString(),
      ]),
    ]
      .map((row) => row.join(','))
      .join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `inactive_users_${period}_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Inactive Users
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
          <Stack spacing={3}>
            {/* Filter Mode Selection */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
                  <InputLabel>Filter Mode</InputLabel>
                  <Select
                    value={filterMode}
                    label="Filter Mode"
                    onChange={(e) => setFilterMode(e.target.value as 'period' | 'custom')}
                  >
                    <MenuItem value="period">Quick Period</MenuItem>
                    <MenuItem value="custom">Custom Date Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={8}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCSV}
              disabled={data.length === 0}
            >
              Export CSV
            </Button>
              </Grid>
            </Grid>

            {/* Period or Custom Date Range */}
            {filterMode === 'period' ? (
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Period</InputLabel>
                    <Select value={period} label="Period" onChange={(e) => setPeriod(e.target.value as any)}>
                      <MenuItem value="7d">Last 7 days</MenuItem>
                      <MenuItem value="30d">Last 30 days</MenuItem>
                      <MenuItem value="365d">Last 365 days</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Inactivity Start Date"
                    value={startDate}
                    onChange={setStartDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Inactivity End Date"
                    value={endDate}
                    onChange={setEndDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              </Grid>
            )}

            <Divider />

            {/* Deposit Filter */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={enableDepositFilter}
                    onChange={(e) => setEnableDepositFilter(e.target.checked)}
                  />
                }
                label="Filter by Deposit Amount"
              />
              {enableDepositFilter && (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Deposit Threshold (KES)"
                      type="number"
                      value={depositThreshold}
                      onChange={(e) => setDepositThreshold(e.target.value)}
                      helperText="Show users who deposited less than this amount"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DatePicker
                      label="Deposit Interval Start"
                      value={depositIntervalStart}
                      onChange={setDepositIntervalStart}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DatePicker
                      label="Deposit Interval End"
                      value={depositIntervalEnd}
                      onChange={setDepositIntervalEnd}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          </Stack>
        </Paper>

        {/* Additional Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Additional Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search MSISDN"
                value={msisdnSearch}
                onChange={(e) => setMsisdnSearch(e.target.value)}
                placeholder="Enter MSISDN..."
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Min Deposits (KES)"
                type="number"
                value={minDeposits}
                onChange={(e) => setMinDeposits(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Max Deposits (KES)"
                type="number"
                value={maxDeposits}
                onChange={(e) => setMaxDeposits(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Min Withdrawals (KES)"
                type="number"
                value={minWithdrawals}
                onChange={(e) => setMinWithdrawals(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Max Withdrawals (KES)"
                type="number"
                value={maxWithdrawals}
                onChange={(e) => setMaxWithdrawals(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Min Transactions"
                type="number"
                value={minTransactions}
                onChange={(e) => setMinTransactions(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Max Transactions"
                type="number"
                value={maxTransactions}
                onChange={(e) => setMaxTransactions(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortField}
                  label="Sort By"
                  onChange={(e) => {
                    const newField = e.target.value as typeof sortField
                    if (sortField === newField) {
                      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
                    } else {
                      setSortField(newField)
                      setSortDirection('desc')
                    }
                  }}
                >
                  <MenuItem value="lastActivity">Last Activity</MenuItem>
                  <MenuItem value="msisdn">MSISDN</MenuItem>
                  <MenuItem value="totalDeposits">Total Deposits</MenuItem>
                  <MenuItem value="totalWithdrawals">Total Withdrawals</MenuItem>
                  <MenuItem value="transactionCount">Transaction Count</MenuItem>
                </Select>
              </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {loading && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Loading inactive users...
          </Typography>
        </Paper>
      )}

      <Paper>
        <Typography variant="h6" sx={{ p: 2 }}>
          Inactive Users ({filteredAndSortedData.length} of {data.length})
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  MSISDN
                  {sortField === 'msisdn' && (
                    <span style={{ marginLeft: 4 }}>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </TableCell>
                <TableCell>
                  Last Activity
                  {sortField === 'lastActivity' && (
                    <span style={{ marginLeft: 4 }}>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </TableCell>
                <TableCell>
                  Total Deposits
                  {sortField === 'totalDeposits' && (
                    <span style={{ marginLeft: 4 }}>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </TableCell>
                <TableCell>
                  Total Withdrawals
                  {sortField === 'totalWithdrawals' && (
                    <span style={{ marginLeft: 4 }}>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </TableCell>
                <TableCell>
                  Transaction Count
                  {sortField === 'transactionCount' && (
                    <span style={{ marginLeft: 4 }}>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {data.length === 0 ? 'No inactive users found' : 'No users match the current filters'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedData.map((user) => (
                  <TableRow key={user.msisdn}>
                    <TableCell>{user.msisdn}</TableCell>
                    <TableCell>
                      {user.lastActivity ? new Date(user.lastActivity).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>KES {user.totalDeposits.toFixed(2)}</TableCell>
                    <TableCell>KES {(user.totalWithdrawals || 0).toFixed(2)}</TableCell>
                    <TableCell>{user.transactionCount}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
    </LocalizationProvider>
  )
}

export default InactiveUsers

